/* Direct Communication — one-off Email / SMS composer client (Pass F2).
 *
 * Backed by DirectMessageController (routes/crm.php). Same audit tables
 * as campaigns; every send appears in Communication History. */

import { API_URL } from "../config";
import { trackedFetch } from "../Components/Loader/GlobalLoader";

const headers = (): HeadersInit => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

export type DirectRecipientType = "student" | "parent" | "staff" | "admin";
export type DirectChannel        = "email" | "sms";
export type DirectClassification = "marketing" | "transactional";

export interface IRecipientRow {
  id: number;
  recipient_type: DirectRecipientType;
  display_name: string;
  email: string | null;
  phone: string | null;
  status?: string | null;
  note?: string | null;
}

export interface ITemplateRow {
  id: number;
  name: string;
  slug: string | null;
  subject: string | null;
  body: string;
  category: string | null;
  is_default: boolean;
}

export interface IPreviewResult {
  recipient: {
    recipient_type: string;
    recipient_id: number | null;
    source_type: string | null;
    source_id: number | null;
    display_name: string;
    email: string | null;
    phone: string | null;
  };
  subject: string | null;
  body: string;
  unresolved: string[];
  channel_available: { email: boolean; sms: boolean };
}

export interface ISendResult {
  audit_id: number;
  channel: "email" | "sms";
  status: string;
  recipient: string;
  address: string;
}

interface Envelope<T> { code: number; status: boolean; message?: string; data: T }

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await trackedFetch(`${API_URL}${path}`, { ...init, headers: headers() });
  const json: Envelope<T> = await res.json();
  if (!json.status) throw new Error(json.message || "Request failed");
  return json.data;
}

/** Server-side recipient search (min 1 char). Never dumps all users. */
export const searchRecipients = (type: DirectRecipientType, q: string, limit = 20) => {
  const qs = new URLSearchParams({ type, q, limit: String(limit) });
  return call<IRecipientRow[]>(`/direct-message/recipients/search?${qs}`, { method: "GET" });
};

export const listTemplates = (channel: DirectChannel) =>
  call<ITemplateRow[]>(`/direct-message/templates?channel=${channel}`, { method: "GET" });

export interface IPreviewInput {
  channel: DirectChannel;
  recipient: { recipient_type: DirectRecipientType; recipient_id: number };
  subject?: string;
  body: string;
}

export const previewDirect = (data: IPreviewInput) =>
  call<IPreviewResult>(`/direct-message/preview`, {
    method: "POST", body: JSON.stringify(data),
  });

export interface ISendEmailInput {
  recipient: { recipient_type: DirectRecipientType; recipient_id: number };
  subject: string;
  body: string;
  classification: DirectClassification;
  idempotency_key?: string;
}

export const sendDirectEmail = (data: ISendEmailInput) =>
  call<ISendResult>(`/direct-message/send-email`, {
    method: "POST", body: JSON.stringify(data),
  });

export interface ISendSmsInput {
  recipient: { recipient_type: DirectRecipientType; recipient_id: number };
  body: string;
  classification: DirectClassification;
  idempotency_key?: string;
}

export const sendDirectSms = (data: ISendSmsInput) =>
  call<ISendResult>(`/direct-message/send-sms`, {
    method: "POST", body: JSON.stringify(data),
  });
